import { UNMATCHED_TERM_ID, useCoursesLoaderData } from "./coursesLoader";
import styles from "./TermInfo.module.scss";
import { FormattedMessage, useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";
import BaseLinkOrButton from "../../../../common/core-ui/BaseLinkOrButton.tsx";
import { useState } from "react";
import TermSwitcherDialog, {
  TermSwitcherOption,
} from "./TermSwitcherDialog.tsx";
import useVirtualStore from "./useVirtualStore.tsx";
import { clsx } from "clsx";
import TermInfoRollup from "./TermInfoRollup.tsx";
import useTermStoreMetrics from "./useTermStoreMetrics.tsx";

export default function TermInfo() {
  const [searchParams, setSearchParams] = useSearchParams();
  const courseData = useCoursesLoaderData();
  const intl = useIntl();
  const { isVirtualStore } = useVirtualStore();
  const { numCourses, totalRequiredMaterials } = useTermStoreMetrics();
  const [openTermSwitcherDialog, setOpenTermSwitcherDialog] =
    useState<boolean>(false);
  const unmatchedCoursesOption = intl.formatMessage({
    defaultMessage: "Other Courses",
  });
  const getUnmatchedCourseTitle = () => {
    return courseData.terms.length > 1
      ? unmatchedCoursesOption
      : intl.formatMessage({ defaultMessage: "All Courses" });
  };
  const getTitle = () => {
    return courseData.currentTerm
      ? courseData.currentTerm.displayName
      : getUnmatchedCourseTitle();
  };
  const getMaterialCount = () => {
    if (isVirtualStore) {
      // note smartling does not support multiple plurals in same string
      const coursesTxt = intl.formatMessage(
        {
          defaultMessage:
            "{numCourses, plural, =1 {1 Course} other {# Courses}}",
        },
        { numCourses }
      );
      const materialsTxt = intl.formatMessage(
        {
          defaultMessage:
            "{totalRequiredMaterials, plural, =1 {1 Required Course Material } other {# Required Course Materials}}",
        },
        { totalRequiredMaterials }
      );
      return intl.formatMessage(
        {
          defaultMessage: "{coursesTxt}, {materialsTxt}",
        },
        { coursesTxt, materialsTxt }
      );
    } else {
      return intl.formatMessage(
        {
          defaultMessage:
            "{materialsCount, plural, =1 {1 Course Material} other {# Course Materials}}",
        },
        { materialsCount: courseData.materialCount }
      );
    }
  };
  const options = courseData.terms.map<TermSwitcherOption>(
    ({ id, displayName }) => ({
      label: id === UNMATCHED_TERM_ID ? unmatchedCoursesOption : displayName,
      value: id,
      // todo add start and end date when available
    })
  );
  const getInitialValue = () => {
    const id = courseData.currentTerm?.id ?? UNMATCHED_TERM_ID;
    return options.find((option) => option.value === id);
  };

  return (
    <div
      className={clsx(
        styles.sizeContainer,
        isVirtualStore && styles.virtualStore
      )}
    >
      <div className={styles.container}>
        <div>
          <div className={styles.titleContainer}>
            <h1 className={styles.name}>{getTitle()}</h1>
            {courseData.terms.length > 1 && (
              <BaseLinkOrButton
                className="vst-link"
                onClick={() => setOpenTermSwitcherDialog(true)}
                data-testid="term-select"
              >
                <FormattedMessage defaultMessage={"Change Term"} />
              </BaseLinkOrButton>
            )}
          </div>
          <div className={styles.materialCount}>{getMaterialCount()}</div>
        </div>
        <TermInfoRollup />
        {openTermSwitcherDialog && (
          <TermSwitcherDialog
            options={options}
            initialVal={getInitialValue()?.value}
            onRequestClose={(id) => {
              setOpenTermSwitcherDialog(false);
              if (id && id !== getInitialValue()?.value) {
                searchParams.set("termId", id.toString());
                setSearchParams(searchParams);
              }
            }}
          />
        )}
      </div>
    </div>
  );
}
